import fs from 'fs/promises'
import path from 'path'
import crypto from 'crypto'
import { Client } from '@botpress/client'
import { auth } from '../auth/index.js'
import {
  AssetFile,
  LocalAssetFile,
  AssetSyncPlan,
  AssetSyncResult,
  AssetSyncItem,
  AssetSyncOperation,
  AssetSyncOptions,
  AssetsIndex,
} from './types.js'
import { AssetsCacheManager } from './cache.js'

export interface AssetManagerOptions {
  projectPath: string
  botId?: string
  workspaceId?: string
}

export class AssetsManager {
  private projectPath: string
  private assetsPath: string
  private client?: Client
  private botId?: string
  private workspaceId?: string
  private cacheManager: AssetsCacheManager

  constructor(options: AssetManagerOptions) {
    this.projectPath = options.projectPath
    this.assetsPath = path.join(this.projectPath, 'assets')
    this.botId = options.botId
    this.workspaceId = options.workspaceId
    this.cacheManager = new AssetsCacheManager(this.projectPath)
  }

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = await auth.getActiveCredentials()

      // Use provided workspaceId or fall back to credentials
      const workspaceId = this.workspaceId || credentials.workspaceId

      if (!workspaceId) {
        throw new Error('No workspace ID found. Please login again with "adk login" or deploy your agent first.')
      }

      if (!this.botId) {
        throw new Error(
          'Bot ID is required for asset operations. Please deploy your agent first or create agent.json with botId and workspaceId.'
        )
      }

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        workspaceId: workspaceId,
        botId: this.botId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  private assertBotId(operation: string): void {
    if (!this.botId) {
      throw new Error(
        `Operation "${operation}" requires a bot ID. ` +
          'Please deploy your agent first or create agent.json with botId and workspaceId.'
      )
    }
  }

  /**
   * Check if assets directory exists
   */
  async hasAssetsDirectory(): Promise<boolean> {
    try {
      const stats = await fs.stat(this.assetsPath)
      return stats.isDirectory()
    } catch {
      return false
    }
  }

  /**
   * Get all local asset files with their metadata
   */
  async getLocalAssets(): Promise<LocalAssetFile[]> {
    if (!(await this.hasAssetsDirectory())) {
      return []
    }

    const files = await this.scanDirectory(this.assetsPath)
    const assets: LocalAssetFile[] = []

    for (const filePath of files) {
      try {
        const stats = await fs.stat(filePath)
        if (stats.isFile()) {
          const relativePath = path.relative(this.assetsPath, filePath)
          const content = await fs.readFile(filePath)
          const hash = this.calculateHash(content)
          const mime = this.getMimeType(filePath)

          assets.push({
            relativePath,
            absolutePath: filePath,
            name: path.basename(filePath),
            size: stats.size,
            mime,
            hash,
            stats: {
              mtime: stats.mtime,
              size: stats.size,
            },
          })
        }
      } catch (error) {
        // Skip files that can't be read
        console.warn(`Warning: Could not read asset file ${filePath}:`, error)
      }
    }

    return assets
  }

  /**
   * Get all remote asset files from Botpress
   */
  async getRemoteAssets(): Promise<AssetFile[]> {
    this.assertBotId('get remote assets')

    const client = await this.getClient()

    try {
      // List files with ADK asset tags (botId is automatically scoped by client)
      const response = await client.listFiles({
        tags: {
          type: 'asset',
          adk: 'true',
        },
      })

      return response.files.map((file) => ({
        path: file.tags?.path || file.key,
        name: path.basename(file.tags?.path || file.key),
        size: file.size || 0,
        mime: file.contentType,
        hash: file.tags?.hash || '',
        createdAt: file.createdAt,
        updatedAt: file.updatedAt,
        fileId: file.id,
        url: file.url || '',
      }))
    } catch (error) {
      throw new Error(`Failed to fetch remote assets: ${error}`)
    }
  }

  /**
   * Create a sync plan comparing local and remote assets
   */
  async createSyncPlan(): Promise<AssetSyncPlan> {
    const [localAssets, remoteAssets] = await Promise.all([this.getLocalAssets(), this.getRemoteAssets()])

    const items: AssetSyncItem[] = []
    const remoteMap = new Map<string, AssetFile>()

    // Map remote assets by path
    for (const remote of remoteAssets) {
      remoteMap.set(remote.path, remote)
    }

    // Check local files against remote
    for (const local of localAssets) {
      const remote = remoteMap.get(local.relativePath)

      if (!remote) {
        // Local file doesn't exist remotely - needs to be created
        items.push({
          operation: AssetSyncOperation.Create,
          localFile: local,
          reason: 'New local file',
        })
      } else if (local.hash !== remote.hash) {
        // File exists but content is different - needs to be updated
        items.push({
          operation: AssetSyncOperation.Update,
          localFile: local,
          remoteFile: remote,
          reason: 'Content changed',
        })
      } else {
        // Files are identical - no operation needed
        items.push({
          operation: AssetSyncOperation.None,
          localFile: local,
          remoteFile: remote,
          reason: 'Up to date',
        })
      }

      // Remove from remote map so we can find orphaned remote files
      remoteMap.delete(local.relativePath)
    }

    // Remaining remote files don't have local counterparts - need to be deleted
    for (const [, remote] of remoteMap) {
      items.push({
        operation: AssetSyncOperation.Delete,
        remoteFile: remote,
        reason: 'Local file deleted',
      })
    }

    // Calculate totals
    const totalCreate = items.filter((i) => i.operation === AssetSyncOperation.Create).length
    const totalUpdate = items.filter((i) => i.operation === AssetSyncOperation.Update).length
    const totalDelete = items.filter((i) => i.operation === AssetSyncOperation.Delete).length
    const hasChanges = totalCreate > 0 || totalUpdate > 0 || totalDelete > 0

    return {
      items,
      totalCreate,
      totalUpdate,
      totalDelete,
      hasChanges,
    }
  }

  /**
   * Execute a sync plan
   */
  async executeSync(plan: AssetSyncPlan, options: AssetSyncOptions = {}): Promise<AssetSyncResult> {
    this.assertBotId('sync assets')

    if (options.dryRun) {
      return {
        applied: false,
        success: [],
        failed: [],
        summary: {
          created: 0,
          updated: 0,
          deleted: 0,
          failed: 0,
        },
      }
    }

    const client = await this.getClient()
    const success: AssetSyncItem[] = []
    const failed: Array<{ item: AssetSyncItem; error: Error }> = []

    for (const item of plan.items) {
      if (item.operation === AssetSyncOperation.None) {
        success.push(item)
        continue
      }

      try {
        switch (item.operation) {
          case AssetSyncOperation.Create:
          case AssetSyncOperation.Update:
            if (item.localFile) {
              await this.uploadAsset(client, item.localFile)
              success.push(item)
            }
            break

          case AssetSyncOperation.Delete:
            if (item.remoteFile?.fileId) {
              await client.deleteFile({ id: item.remoteFile.fileId })
              success.push(item)
            }
            break
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        failed.push({ item, error: err })

        if (options.bailOnFailure) {
          break
        }
      }
    }

    return {
      applied: true,
      success,
      failed,
      summary: {
        created: success.filter((i) => i.operation === AssetSyncOperation.Create).length,
        updated: success.filter((i) => i.operation === AssetSyncOperation.Update).length,
        deleted: success.filter((i) => i.operation === AssetSyncOperation.Delete).length,
        failed: failed.length,
      },
    }
  }

  /**
   * Upload a local asset to Botpress
   */
  private async uploadAsset(client: Client, localFile: LocalAssetFile): Promise<void> {
    const content = await fs.readFile(localFile.absolutePath)

    await client.uploadFile({
      key: localFile.relativePath,
      content,
      contentType: localFile.mime,
      tags: {
        type: 'asset',
        adk: 'true',
        path: localFile.relativePath,
        hash: localFile.hash,
        name: localFile.name,
        size: localFile.size.toString(),
      },
      index: true, // Make file searchable
    })
  }

  /**
   * Generate TypeScript types for assets
   */
  async generateTypes(): Promise<string> {
    const localAssets = await this.getLocalAssets()

    // Generate union type of all asset paths
    const pathUnion = localAssets.map((asset) => `"${asset.relativePath}"`).join(' | ')

    const paths = localAssets.map((asset) => `  "${asset.relativePath}": "${asset.relativePath}";`).join('\n')

    return `// Auto-generated asset types
import { Asset } from '@botpress/runtime';

export type AssetPaths = ${pathUnion || 'never'};

export interface AssetPathMap {
${paths}
}

// Runtime asset access
declare global {
  const assets: {
    get<T extends AssetPaths>(path: T): Asset;
    list(): Asset[];
    getSyncStatus(): { 
      synced: boolean; 
      neverSynced: string[]; 
      stale: string[];
      upToDate: string[];
    };
  };
}
`
  }

  /**
   * Create an assets index file
   */
  async createAssetsIndex(): Promise<AssetsIndex> {
    const remoteAssets = await this.getRemoteAssets()

    return {
      files: remoteAssets,
      generatedAt: new Date().toISOString(),
      totalFiles: remoteAssets.length,
      totalSize: remoteAssets.reduce((sum, asset) => sum + asset.size, 0),
    }
  }

  /**
   * Get enriched local assets with remote metadata when available
   * Uses cache to avoid unnecessary API calls
   */
  async getEnrichedLocalAssets(): Promise<AssetFile[]> {
    const localAssets = await this.getLocalAssets()
    const enrichedAssets: AssetFile[] = []

    // Try to get client if credentials are available
    let remoteAssetsMap: Map<string, AssetFile> = new Map()
    try {
      if (this.botId) {
        const remoteAssets = await this.getRemoteAssets()
        remoteAssetsMap = new Map(remoteAssets.map((asset) => [asset.path, asset]))
      }
    } catch (error) {
      // If we can't fetch remote assets, we'll use cache or placeholders
      console.debug('Could not fetch remote assets:', error)
    }

    for (const localAsset of localAssets) {
      const cachedEntry = await this.cacheManager.getEntry(localAsset.relativePath)
      const remoteAsset = remoteAssetsMap.get(localAsset.relativePath)

      if (cachedEntry) {
        // We have cached metadata - use it and update the cache with current hashes
        enrichedAssets.push(cachedEntry.metadata)
        await this.cacheManager.setEntry(
          localAsset.relativePath,
          localAsset.hash,
          cachedEntry.remoteHash,
          cachedEntry.metadata
        )
      } else if (remoteAsset) {
        // No cache but we have remote - use remote and cache it
        enrichedAssets.push(remoteAsset)
        await this.cacheManager.setEntry(localAsset.relativePath, localAsset.hash, remoteAsset.hash, remoteAsset)
      } else {
        // No cache and no remote - create placeholder
        const placeholderAsset: AssetFile = {
          url: `__PLACEHOLDER_URL_${localAsset.relativePath}__`,
          path: localAsset.relativePath,
          size: localAsset.size,
          name: localAsset.name,
          mime: localAsset.mime,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          fileId: `__PLACEHOLDER_ID_${localAsset.relativePath}__`,
          hash: localAsset.hash,
        }
        enrichedAssets.push(placeholderAsset)
      }
    }

    return enrichedAssets
  }

  // Private utility methods

  private async scanDirectory(dir: string, files: string[] = []): Promise<string[]> {
    const items = await fs.readdir(dir)

    for (const item of items) {
      const fullPath = path.join(dir, item)
      const stats = await fs.stat(fullPath)

      if (stats.isDirectory()) {
        await this.scanDirectory(fullPath, files)
      } else {
        files.push(fullPath)
      }
    }

    return files
  }

  private calculateHash(content: Buffer): string {
    return crypto.createHash('sha256').update(content).digest('hex')
  }

  private getMimeType(filePath: string): string {
    const ext = path.extname(filePath).toLowerCase()

    const mimeTypes: Record<string, string> = {
      '.png': 'image/png',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.gif': 'image/gif',
      '.svg': 'image/svg+xml',
      '.webp': 'image/webp',
      '.pdf': 'application/pdf',
      '.txt': 'text/plain',
      '.md': 'text/markdown',
      '.html': 'text/html',
      '.css': 'text/css',
      '.js': 'application/javascript',
      '.json': 'application/json',
      '.xml': 'application/xml',
      '.zip': 'application/zip',
      '.mp4': 'video/mp4',
      '.webm': 'video/webm',
      '.mp3': 'audio/mpeg',
      '.wav': 'audio/wav',
      '.ogg': 'audio/ogg',
    }

    return mimeTypes[ext] || 'application/octet-stream'
  }
}
